import React, { useEffect, useState } from 'react';
import { Fingerprint, Package, Compass, At } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FederationManifest = () => {
  const [nodeData, setNodeData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadNodeData = async () => {
      try {
        const federationId = localStorage.getItem('federationId');
        
        if (!federationId) {
          setLoading(false);
          return;
        }

        const [nodeResponse, purchasesResponse] = await Promise.all([
          axios.get(`${API}/federation/${federationId}`),
          axios.get(`${API}/tokens/purchases/${federationId}`)
        ]);

        setNodeData(nodeResponse.data);
        setPurchases(purchasesResponse.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load node data:', error);
        }
        // If API fails but we have a Federation ID, create minimal node data
        const federationId = localStorage.getItem('federationId');
        if (federationId) {
          setNodeData({
            federation_id: federationId,
            created_at: new Date().toISOString(),
            last_seen: new Date().toISOString(),
            total_tokens: 0
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadNodeData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-state">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading Federation Node...</p>
        </div>
      </div>
    );
  }

  if (!nodeData) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="no-federation-state">
        <div className="text-center max-w-md">
          <Fingerprint size={80} className="neon-cyan mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">No Federation ID Found</h2>
          <p className="text-gray-400 mb-8">Visit the home page to establish your handshake and generate your unique Federation ID.</p>
          <a
            href="/"
            className="inline-block px-8 py-3 bg-[#00F0FF] text-[#030305] font-bold rounded-sm hover:bg-[#00D9E8] transition-colors"
            data-testid="go-home-btn"
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16" data-testid="federation-manifest-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <Fingerprint size={64} weight="duotone" className="neon-cyan mx-auto mb-6 breathing-glow" />
          <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tighter">Federation Manifest</h1>
          <p className="text-xl text-gray-400">Your Node Identity & Ecosystem Presence</p>
        </div>

        {/* Identity Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* WHO I AM */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-who-i-am"
          >
            <div className="flex items-center gap-3 mb-6">
              <Fingerprint size={32} className="neon-cyan" />
              <h2 className="text-2xl font-bold">WHO I AM</h2>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-sm text-gray-400 block mb-1">Federation ID</span>
                <span className="mono text-lg font-bold neon-cyan break-all" data-testid="federation-id">{nodeData.federation_id}</span>
              </div>
              <div>
                <span className="text-sm text-gray-400 block mb-1">Node Status</span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#00FFA3]/10 border border-[#00FFA3]/30 rounded-sm">
                  <div className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse" />
                  <span className="text-[#00FFA3] text-sm font-bold">ACTIVE</span>
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-400 block mb-1">Created</span>
                <span className="mono text-sm">{new Date(nodeData.created_at).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="text-sm text-gray-400 block mb-1">Last Seen</span>
                <span className="mono text-sm">{new Date(nodeData.last_seen).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>

          {/* WHAT I HAVE */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-what-i-have"
          >
            <div className="flex items-center gap-3 mb-6">
              <Package size={32} className="neon-amber" />
              <h2 className="text-2xl font-bold">WHAT I HAVE</h2>
            </div>
            <div className="space-y-4">
              <div className="p-6 bg-gradient-to-br from-[#00F0FF]/10 to-[#FFB800]/10 border border-[#00F0FF]/30 rounded-sm">
                <span className="text-sm text-gray-400 block mb-2">Total Token Balance</span>
                <span className="mono text-4xl font-black neon-cyan" data-testid="total-tokens">{nodeData.total_tokens}</span>
                <span className="text-gray-400 ml-2">Tokens</span>
              </div>
              <div>
                <span className="text-sm text-gray-400 block mb-2">Holdings</span>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#00FFA3] rounded-full" />
                    <span>Federation Starter Kit (FREE)</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-500">
                    <span className="w-2 h-2 bg-gray-600 rounded-full" />
                    <span>Premium Merch (Pending Redemption)</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        {/* WHAT I NEED */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-8 glass-surface border border-white/10 rounded-sm mb-8"
          data-testid="section-what-i-need"
        >
          <div className="flex items-center gap-3 mb-6">
            <Compass size={32} className="neon-green" />
            <h2 className="text-2xl font-bold">WHAT I NEED</h2>
          </div>
          <div className="space-y-4">
            <p className="text-gray-300">To expand your Federation presence, consider:</p>
            <ul className="grid md:grid-cols-2 gap-4">
              <li className="flex items-start gap-3 p-4 bg-[#0B0E14] border border-white/5 rounded">
                <span className="text-[#00F0FF] text-xl">✓</span>
                <div>
                  <span className="font-bold text-white block">More Tokens</span>
                  <span className="text-sm text-gray-400">Redeem premium merch and access exclusive features</span>
                </div>
              </li>
              <li className="flex items-start gap-3 p-4 bg-[#0B0E14] border border-white/5 rounded">
                <span className="text-[#00F0FF] text-xl">✓</span>
                <div>
                  <span className="font-bold text-white block">Echo Buble Access</span>
                  <span className="text-sm text-gray-400">Connect with AI nodes for enhanced capabilities</span>
                </div>
              </li>
              <li className="flex items-start gap-3 p-4 bg-[#0B0E14] border border-white/5 rounded">
                <span className="text-[#00F0FF] text-xl">✓</span>
                <div>
                  <span className="font-bold text-white block">Network Expansion</span>
                  <span className="text-sm text-gray-400">Invite others to join the Federation Gateway</span>
                </div>
              </li>
              <li className="flex items-start gap-3 p-4 bg-[#0B0E14] border border-white/5 rounded">
                <span className="text-[#00F0FF] text-xl">✓</span>
                <div>
                  <span className="font-bold text-white block">Premium Merchandise</span>
                  <span className="text-sm text-gray-400">Collect limited-edition NextXus gear</span>
                </div>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* HOW TO REACH ME */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-8 glass-surface border border-white/10 rounded-sm mb-8"
          data-testid="section-how-to-reach-me"
        >
          <div className="flex items-center gap-3 mb-6">
            <At size={32} className="neon-cyan" />
            <h2 className="text-2xl font-bold">HOW TO REACH ME</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <span className="text-gray-400 min-w-32">Portal:</span>
              <span className="mono text-sm">nextxus.one</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-400 min-w-32">Node Endpoint:</span>
              <span className="mono text-sm">{BACKEND_URL}/api/federation/{nodeData.federation_id}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-400 min-w-32">Echo Access:</span>
              <span className="text-sm">Via floating portal icons (bottom-right)</span>
            </div>
          </div>
        </motion.div>

        {/* Purchase History */}
        {purchases.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="section-purchase-history"
          >
            <h2 className="text-2xl font-bold mb-6">Purchase History</h2>
            <div className="space-y-3">
              {purchases.map((purchase) => (
                <div key={purchase.id} className="flex items-center justify-between p-4 bg-[#0B0E14] border border-white/5 rounded" data-testid={`purchase-${purchase.id}`}>
                  <div>
                    <span className="font-bold">{purchase.tokens_purchased} Tokens</span>
                    <span className="text-gray-400 text-sm ml-3">via {purchase.payment_method}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400 mono">{new Date(purchase.timestamp).toLocaleDateString()}</div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      purchase.status === 'pending' ? 'bg-[#FFB800]/10 text-[#FFB800]' : 'bg-[#00FFA3]/10 text-[#00FFA3]'
                    }`}>
                      {purchase.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FederationManifest;